import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { Formik } from "formik";
import { mapValues, parseDate, useAmsSelector } from "@awe-web/shared/lib";
import { newGetInitialValues } from "@awe-web/shared/lib";
import { useInitialize } from "../../util/react.js";
import { ScreenLoading } from "../../components/screen.js";
import { getSelectStationAndTimeFormValidate, getFinalStations } from "../store.js";
import {
  initializeSelectStationAndTimeForm,
  submitSelectStationAndTimeForm
} from "../actions.js";
import SelectStationAndTimeForm from "./select_station_and_time_form";

function InternalSelectStationAndTime({ className, onSubmit }) {
  const validate = useSelector((state) => getSelectStationAndTimeFormValidate(state));

  const stations = useSelector((state) => getFinalStations(state));
  const inialValuesFactory = useAmsSelector((state) => newGetInitialValues("selectStationAndTime")(state));

  const initialValues = useMemo(() => {
    const values = inialValuesFactory({
      coStationId: null,
      ciStationId: null,
      coDateTime: null,
      ciDateTime: null
    });

    if (values.coDateTime && !(values.coDateTime instanceof Date)) {
      values.coDateTime = parseDate(values.coDateTime);

    }
    if (values.ciDateTime && !(values.ciDateTime instanceof Date)) {
      values.ciDateTime = parseDate(values.ciDateTime);
    }
    return values;
  }, [inialValuesFactory]);

  // Mark all values as 'touched' that have an initial value so that error
  // messages are shown e.g. for ciDateTime when coStationId changes.
  const initialTouched = mapValues(initialValues, (value) => Boolean(value));

  return (
    <Formik
      initialValues={initialValues}
      initialTouched={initialTouched}
      validate={validate}
      onSubmit={onSubmit}
    >
      {(formikProps) => (
        <SelectStationAndTimeForm
          {...formikProps}
          className={className}
          coStations={stations.toJS()}
        />
      )}
    </Formik>
  );
}

export default function SelectStationAndTime({ className, reservationPath }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onSubmit = useCallback(async (formValues) => {
    const nextRoute = await dispatch(submitSelectStationAndTimeForm(formValues, { reservationPath }));
    navigate(nextRoute);
  }, [dispatch, navigate, reservationPath]);

  const init = useCallback(async () => {
    await dispatch(initializeSelectStationAndTimeForm({ reservationPath }));
  }, [dispatch, reservationPath]);

  const isReady = useInitialize(init);
  if (!isReady) {
    return <ScreenLoading />;
  }

  else {
    return <InternalSelectStationAndTime className={className} onSubmit={onSubmit}></InternalSelectStationAndTime>;
  }
}

SelectStationAndTime.propTypes = {
  className: PropTypes.string,
  reservationPath: PropTypes.oneOf(["B2B", "B2C"]).isRequired
};
